import { useState, useEffect } from 'react'
import { motion, useAnimation } from 'framer-motion'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import ReportIconLight from '../../assets/animation/ReportIconLight.svg'
import ReportIconDark from '../../assets/animation/ReportIconDark.svg'
import ReportDelete from '../../assets/animation/ReportDelete.svg'
import BinBody from '../../assets/animation/BinBody.svg'
import BinLid from '../../assets/animation/BinLid.svg'
import { fsColors } from '../../shared/constants'
import { useAppSelector } from '../../store/hooks'
import '../../App.css'

const FsReportModal = ({
  onClose,
  confirmAction,
  cloneReport,
  deleteReport,
}: {
  onClose: any
  confirmAction: { reportId: number; action: 'delete' | 'clone' } | null
  cloneReport: (reportId: number) => void
  deleteReport: (reportId: number) => void
}) => {
  const { currentTheme } = useAppSelector((state) => state.currentUser)
  const { t } = useTranslation()
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  const reportLightControls = useAnimation()
  const reportDarkControls = useAnimation()
  const reportDeleteControls = useAnimation()
  const binLidControls = useAnimation()

  useEffect(() => {
    const runAnimation = async () => {
      if (confirmAction?.action === 'clone') {
        await Promise.all([
          reportLightControls.start({
            opacity: 1,
            transition: { duration: 0.5, ease: 'easeInOut' },
          }),
          reportDarkControls.start({
            opacity: 1,
            transition: { duration: 0.5, ease: 'easeInOut' },
          }),
        ])
        reportDarkControls.start({
          x: '-10%',
          y: '-10%',
          transition: { duration: 1.5, ease: 'easeInOut' },
        })
        reportLightControls.start({
          x: '20%',
          y: '-20%',
          transition: { duration: 1.5, ease: 'easeInOut' },
        })
      } else if (confirmAction?.action === 'delete') {
        reportDeleteControls.start({
          x: '50%',
          y: '0%',
          opacity: 1,
          transition: { duration: 1.5, ease: 'easeInOut' },
        })
        binLidControls.start({
          rotate: 70,
          transformOrigin: 'right',
          transition: { duration: 0.5, ease: 'easeInOut' },
        })
      }
    }

    runAnimation()
  }, [confirmAction])

  const handleDeleteClick = async () => {
    if (confirmAction) {
      setIsDeleting(true)

      await reportDeleteControls.start({
        x: '70%',
        y: '110%',
        rotate: 20,
        opacity: 0.2,
        transition: { duration: 1, ease: 'easeInOut' },
      })
      await binLidControls.start({
        rotate: 0,
        transition: { duration: 0.5, ease: 'easeInOut' },
      })

      deleteReport(confirmAction?.reportId)
      setTimeout(() => {
        setIsDeleting(false)
        onClose()
      }, 1700)
    }
  }

  switch (confirmAction?.action) {
    case 'clone':
      return (
        <Box className="spinner-container">
          <Stack
            sx={{
              width: 500,
              height: 280,
              flexDirection: 'column',
              alignItems: 'flex-end',
              background: currentTheme === 'light' ? '#fff' : fsColors.fsGray.colors[0],
              borderRadius: 2,
              boxShadow: 4,
            }}
          >
            <Stack
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                gap: 1,
              }}
            >
              <Typography variant="h6" mx={2} mt={2}>
                {t('clone.question')}
              </Typography>
              <Typography variant="body1" mx={2} mt={-1}>
                {t('report.duplicate.explain')}
              </Typography>

              <Stack style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <motion.img
                  id="ReportLight"
                  src={ReportIconLight}
                  className="report-light"
                  alt="ReportIconLight"
                  initial={{ opacity: 0 }}
                  animate={reportLightControls}
                  style={{ width: '60px', height: '60px' }}
                />

                <motion.img
                  id="ReportDark"
                  src={ReportIconDark}
                  className="report-dark"
                  alt="ReportIconDark"
                  initial={{ opacity: 0 }}
                  animate={reportDarkControls}
                  style={{ width: '60px', height: '60px' }}
                />
              </Stack>
            </Stack>
            <Stack sx={{ display: 'flex', flexDirection: 'row' }}>
              <Button
                variant="outlined"
                sx={{
                  mt: 2,
                  mr: 2,
                  mb: 2,
                }}
                onClick={onClose}
              >
                {t('cancel')}
              </Button>
              <Button
                variant="contained"
                sx={{
                  mt: 2,
                  mr: 2,
                  mb: 2,
                }}
                onClick={() => cloneReport(confirmAction.reportId)}
              >
                {t('clone')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )
    case 'delete':
      return (
        <Box className="spinner-container">
          <Stack
            sx={{
              width: 500,
              height: 280,
              flexDirection: 'column',
              alignItems: 'flex-end',
              background: currentTheme === 'light' ? '#fff' : fsColors.fsGray.colors[0],
              borderRadius: 2,
              boxShadow: 4,
            }}
          >
            <Stack
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                gap: 1,
              }}
            >
              <Stack sx={{ flexDirection: 'row', mx: 2, mt: 2 }}>
                <WarningRoundedIcon />
                <Typography variant="h6" mt={-0.3} ml={1}>
                  {t('delete.question')}
                </Typography>
              </Stack>
              <Typography variant="body1" mx={2} mt={-1} mb={1}>
                {t('delete.confirm.message')}
              </Typography>

              <Stack style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <motion.img
                  id="ReportDelete"
                  initial={{ opacity: 0 }}
                  animate={reportDeleteControls}
                  src={ReportDelete}
                  className="report-delete"
                  alt="ReportDelete"
                  style={{ width: '40px', height: '40px' }}
                />
                <motion.img
                  id="BinLid"
                  animate={binLidControls}
                  src={BinLid}
                  className="bin-lid"
                  alt="BinLid"
                  style={{ width: '50px', height: '50px' }}
                />
                <img
                  id="BinBody"
                  src={BinBody}
                  className="bin-body"
                  alt="BinBody"
                  style={{ width: '50px', height: '50px' }}
                />
              </Stack>
            </Stack>
            <Stack sx={{ display: 'flex', flexDirection: 'row' }}>
              <Button
                variant="outlined"
                sx={{
                  mt: 2,
                  mr: 2,
                  mb: 2,
                  borderColor: fsColors.fsDarkBlue.colors[1],
                  textTransform: 'none',
                  color: currentTheme === 'light' ? fsColors.fsDarkBlue.colors[1] : '#fff',
                  backgroundColor: fsColors.fsGray.colors[5],
                  transition: '0.5s all ease-in-out',
                  ':hover': {
                    backgroundColor: fsColors.fsDarkBlue.colors[2],
                    borderColor: fsColors.fsDarkBlue.colors[2],
                    color: '#fff',
                  },
                  ':disabled': {
                    backgroundColor: fsColors.fsGray.colors[2],
                    borderColor: fsColors.fsGray.colors[2],
                    color: fsColors.fsGray.colors[1],
                  },
                }}
                onClick={onClose}
                disabled={isDeleting}
              >
                {t('cancel')}
              </Button>
              <Button
                variant="outlined"
                sx={{
                  mt: 2,
                  mr: 2,
                  mb: 2,
                  borderColor: fsColors.fsDarkBlue.colors[1],
                  textTransform: 'none',
                  color: '#fff',
                  backgroundColor: fsColors.fsDarkBlue.colors[1],
                  transition: '0.5s all ease-in-out',
                  ':hover': {
                    backgroundColor: '#a84432',
                    borderColor: '#a84432',
                    color: '#fff',
                  },
                  ':disabled': {
                    backgroundColor: fsColors.fsGray.colors[2],
                    borderColor: fsColors.fsGray.colors[2],
                    color: fsColors.fsGray.colors[1],
                  },
                }}
                onClick={handleDeleteClick}
                disabled={isDeleting}
              >
                {t(isDeleting ? 'deleting' : 'delete')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )
    default:
      return null
  }
}

export default FsReportModal
